import { useMemo } from 'react';

import { PaymentMethod } from '@order/features/ordering';

import { usePaymentMethods } from '../../../../hooks';
import { useGiftCardPurchasePaymentProfileQuery } from '../../graphql/GiftCardCheckout.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useGiftCardPaymentMethods = (params: UseGiftCardPaymentMethodsParams) => {
  const { pause } = params;

  // ─── Queries ─────────────────────────────────────────────────────────

  const { paymentMethods, isLoadingPaymentMethods } = usePaymentMethods({
    requestPolicy: 'network-only',
    pause,
  });

  const [paymentProfile] = useGiftCardPurchasePaymentProfileQuery({
    requestPolicy: 'network-only',
    pause,
  });

  // ─── Derived Data ────────────────────────────────────────────────────

  const defaultCardId = paymentProfile.data?.paymentProfile?.defaultCardId;

  const defaultPaymentMethodId = useMemo(() => {
    return getDefaultPaymentMethodId({
      defaultCardId,
      paymentMethods,
    });
  }, [defaultCardId, paymentMethods]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    paymentMethods,
    defaultPaymentMethodId,
    isLoadingPaymentMethods: isLoadingPaymentMethods || paymentProfile.fetching,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getDefaultPaymentMethodId(params: GetDefaultPaymentMethodParams): string {
  const { defaultCardId, paymentMethods } = params;

  const maybeDefaultPaymentMethod = paymentMethods.find((method) => {
    const paymentMethodId = method.id;

    return paymentMethodId === defaultCardId;
  })?.id;

  const maybeFirstAvailablePaymentMethod = paymentMethods[0]?.id;

  return maybeDefaultPaymentMethod ?? maybeFirstAvailablePaymentMethod;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseGiftCardPaymentMethodsParams = {
  pause: boolean;
};

type GetDefaultPaymentMethodParams = {
  defaultCardId?: string | null;
  paymentMethods: readonly PaymentMethod[];
};
